import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile, parseProfileForFormula } from '../services/profileService';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import CholFormula from '../formula/Chol';
import HdlFormula from '../formula/Hdl';
import LdlFormula from '../formula/Ldl';
import TgFormula from '../formula/Tg';
import { STATUS_COLORS } from '../utils/healthStatusColors';
import { fetchLabDataByNameFallbacks as sharedFetchLabDataByNameFallbacks } from '../utils/labRecordUtils';
import { buildLipidStatusSummary } from '../utils/lipidStatusUtils';
import { mergeLipidItemsWithMemberLab } from '../utils/lipidDataUtils';

const LipidContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#34A853'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LipidIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 4px 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34A853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const LipidPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [cholesterolData, setCholesterolData] = useState([]);
  const [hdlData, setHdlData] = useState([]);
  const [ldlData, setLdlData] = useState([]);
  const [triglycerideData, setTriglycerideData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [statusCardColor, setStatusCardColor] = useState(STATUS_COLORS.gray);
  const [latestValues, setLatestValues] = useState({
    cholesterol: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    hdl: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    ldl: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    triglyceride: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray }
  });
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    cholesterol: '',
    hdl: '',
    ldl: '',
    triglyceride: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      cholesterol: record.cholesterol || '',
      hdl: record.hdl || '',
      ldl: record.ldl || '',
      triglyceride: record.triglyceride || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {

      const updates = [];

      if (editForm.cholesterol && editForm.cholesterol !== editingRecord.cholesterol) {
        updates.push(updateLabData(editingRecord.rawDate, 'chol', editForm.cholesterol, 'h'));
      }
      if (editForm.hdl && editForm.hdl !== editingRecord.hdl) {
        updates.push(updateLabData(editingRecord.rawDate, 'hdl', editForm.hdl, 'h'));
      }
      if (editForm.ldl && editForm.ldl !== editingRecord.ldl) {
        updates.push(updateLabData(editingRecord.rawDate, 'ldl', editForm.ldl, 'h'));
      }
      if (editForm.triglyceride && editForm.triglyceride !== editingRecord.triglyceride) {
        updates.push(updateLabData(editingRecord.rawDate, 'triglycerideresult', editForm.triglyceride, 'h'));
      }

      await Promise.all(updates);

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadLipidData();
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const calculateRisk = (cholesterol, hdl, ldl, triglyceride) => {
    const cholVal = parseFloat(cholesterol) || 0;
    const hdlVal = parseFloat(hdl) || 0;
    const ldlVal = parseFloat(ldl) || 0;
    const trigVal = parseFloat(triglyceride) || 0;

    if (!cholesterol && !hdl && !ldl && !triglyceride) return '-';

    const isCholAbnormal = cholVal > 0 && cholVal >= 200;
    const isHdlAbnormal = hdlVal > 0 && hdlVal < 40;
    const isLdlAbnormal = ldlVal > 0 && ldlVal >= 130;
    const isTrigAbnormal = trigVal > 0 && trigVal >= 150;

    if (isCholAbnormal || isHdlAbnormal || isLdlAbnormal || isTrigAbnormal) {
      return 'ผิดปกติ';
    }

    return 'ปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadLipidData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);

      setProfileData({ age, gender });

      // ดึงข้อมูลจาก GetLabDataByName API
      const [cholesterolResponse, hdlResponse, ldlResponse, triglycerideResponse] = await Promise.all([
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['chol', 'cholesterol', 'cho', 'cholesterolresult', 'lipidprofilecholesterol', 'lipidprofilecholesterolresult', 'totalcholesterol', 'totalcholesterolresult']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['hdl', 'hdlcholesterolresult', 'lipidprofilehdl', 'lipidprofilehdlresult']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['ldl', 'ldlcholesterolresult', 'lipidprofileldl', 'lipidprofileldlresult']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'triglycerideresult',
          'lipidprofiletriglycerideresult',
          'lipidprofiletriglyceride',
          'triglyceride',
          'tg',
          'tri'
        ]),
      ]);

      let cholesterolArray = cholesterolResponse || [];
      let hdlArray = hdlResponse || [];
      let ldlArray = ldlResponse || [];
      let triglycerideArray = triglycerideResponse || [];

      try {
        const memberLabData = await getMemberLabData();
        const mergedLipidItems = mergeLipidItemsWithMemberLab({
          cholesterolItems: cholesterolArray,
          hdlItems: hdlArray,
          ldlItems: ldlArray,
          triglycerideItems: triglycerideArray,
          memberLabData,
        });

        cholesterolArray = mergedLipidItems.cholesterolItems;
        hdlArray = mergedLipidItems.hdlItems;
        ldlArray = mergedLipidItems.ldlItems;
        triglycerideArray = mergedLipidItems.triglycerideItems;
      } catch (e) {
      }


      const recordsMap = new Map();

      cholesterolArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, cholesterol: null, hdl: null, ldl: null, triglyceride: null });
        }
        if (recordsMap.get(date).cholesterol === null) recordsMap.get(date).cholesterol = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      hdlArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, cholesterol: null, hdl: null, ldl: null, triglyceride: null });
        }
        if (recordsMap.get(date).hdl === null) recordsMap.get(date).hdl = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      ldlArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, cholesterol: null, hdl: null, ldl: null, triglyceride: null });
        }
        if (recordsMap.get(date).ldl === null) recordsMap.get(date).ldl = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      triglycerideArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, cholesterol: null, hdl: null, ldl: null, triglyceride: null });
        }
        if (recordsMap.get(date).triglyceride === null) recordsMap.get(date).triglyceride = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      setAllRecords(combinedRecords);

      const sortedCombinedRecords = [...combinedRecords].sort(
        (a, b) => new Date(a.rawDate) - new Date(b.rawDate)
      );

      const processedCholesterol = sortedCombinedRecords.map(record => ({
        date: record.date || record.rawDate?.split('T')[0].split(' ')[0] || '',
        value: record.cholesterol != null ? parseFloat(record.cholesterol) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedHdl = sortedCombinedRecords.map(record => ({
        date: record.date || record.rawDate?.split('T')[0].split(' ')[0] || '',
        value: record.hdl != null ? parseFloat(record.hdl) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedLdl = sortedCombinedRecords.map(record => ({
        date: record.date || record.rawDate?.split('T')[0].split(' ')[0] || '',
        value: record.ldl != null ? parseFloat(record.ldl) || 0 : null,
        rawDate: record.rawDate
      }));

      const processedTriglyceride = sortedCombinedRecords.map(record => ({
        date: record.date || record.rawDate?.split('T')[0].split(' ')[0] || '',
        value: record.triglyceride != null ? parseFloat(record.triglyceride) || 0 : null,
        rawDate: record.rawDate
      }));

      setCholesterolData(processedCholesterol);
      setHdlData(processedHdl);
      setLdlData(processedLdl);
      setTriglycerideData(processedTriglyceride);

      const lipidSummary = await buildLipidStatusSummary({
        cholesterolItems: cholesterolArray,
        hdlItems: hdlArray,
        ldlItems: ldlArray,
        triglycerideItems: triglycerideArray,
        age,
        gender,
        formulas: {
          chol: CholFormula,
          hdl: HdlFormula,
          ldl: LdlFormula,
          tg: TgFormula,
        },
      });

      if (lipidSummary.latestVisitDate) {
        setLatestDate(formatThaiDate(lipidSummary.latestVisitDate));
      }

      setLatestValues(lipidSummary.latestValues);
      setStatusCardColor(lipidSummary.cardColor);

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLipidData();
  }, []);

  return (
    <LipidContainer>
      <MainHeader />

      <TopBar>ไขมัน</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ไขมัน (Lipid Profile)</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check which values are abnormal (not green)
              const hasData =
                latestValues.cholesterol.value !== '-' ||
                latestValues.hdl.value !== '-' ||
                latestValues.ldl.value !== '-' ||
                latestValues.triglyceride.value !== '-';
              const isCholAbnormal = latestValues.cholesterol.value !== '-' && latestValues.cholesterol.color === STATUS_COLORS.red;
              const isHdlAbnormal = latestValues.hdl.value !== '-' && latestValues.hdl.color === STATUS_COLORS.red;
              const isLdlAbnormal = latestValues.ldl.value !== '-' && latestValues.ldl.color === STATUS_COLORS.red;
              const isTrigAbnormal = latestValues.triglyceride.value !== '-' && latestValues.triglyceride.color === STATUS_COLORS.red;

              const hasAbnormal = isCholAbnormal || isHdlAbnormal || isLdlAbnormal || isTrigAbnormal;

              return (
                <StatusCard $bgColor={statusCardColor}>
                  <LipidIcon src="./ไขมันสี.png" alt="ไขมัน" />
                  <StatusInfo>
                    {!hasData ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูล</StatusTitle>
                    ) : !hasAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        {isCholAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Cholesterol</AbnormalName>
                            <AbnormalValue>{latestValues.cholesterol.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.cholesterol.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isHdlAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>HDL</AbnormalName>
                            <AbnormalValue>{latestValues.hdl.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.hdl.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isLdlAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>LDL</AbnormalName>
                            <AbnormalValue>{latestValues.ldl.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.ldl.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isTrigAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Triglyceride</AbnormalName>
                            <AbnormalValue>{latestValues.triglyceride.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.triglyceride.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            {/* Cholesterol Chart */}
            <ChartSection>
              <ChartTitle>Total Cholesterol</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (&lt; 200)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (≥ 200)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={cholesterolData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 300]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={200} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={200} y2={300} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* HDL Chart */}
            <ChartSection>
              <ChartTitle>HDL (Good Cholesterol)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 40)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (≥ 40)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={hdlData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={40} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={40} y2={100} fill="#E8F5E9" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#34A853"
                    strokeWidth={2}
                    dot={{ fill: '#34A853', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* LDL Chart */}
            <ChartSection>
              <ChartTitle>LDL (Bad Cholesterol)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (&lt; 130)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (≥ 130)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={ldlData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 200]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={130} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={130} y2={200} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FFA726"
                    strokeWidth={2}
                    dot={{ fill: '#FFA726', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Triglyceride Chart */}
            <ChartSection>
              <ChartTitle>Triglyceride</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (&lt; 150)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (≥ 150)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={triglycerideData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 300]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={150} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={150} y2={300} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#9C27B0"
                    strokeWidth={2}
                    dot={{ fill: '#9C27B0', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.cholesterol) values.push(`Chol: ${record.cholesterol}`);
                      if (record.hdl) values.push(`HDL: ${record.hdl}`);
                      if (record.ldl) values.push(`LDL: ${record.ldl}`);
                      if (record.triglyceride) values.push(`Trig: ${record.triglyceride}`);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDisplayDate(record.date)}</TableCell>
                          <TableCell>{values.join(', ') || '-'}</TableCell>
                          <TableCell>{calculateRisk(record.cholesterol, record.hdl, record.ldl, record.triglyceride)}</TableCell>
                          <TableCell>
                            <EditButton onClick={() => handleEditClick(record)}>
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </EditButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>Total Cholesterol (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.cholesterol}
                onChange={(e) => setEditForm({ ...editForm, cholesterol: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>HDL (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.hdl}
                onChange={(e) => setEditForm({ ...editForm, hdl: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>LDL (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.ldl}
                onChange={(e) => setEditForm({ ...editForm, ldl: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>Triglyceride (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.triglyceride}
                onChange={(e) => setEditForm({ ...editForm, triglyceride: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </LipidContainer>
  );
};

export default LipidPage;
